import { getCurrencySymbol, formatCurrency } from '@angular/common';
import * as moment from 'moment-timezone';

import { Package, Availability } from '../models/venues.model';

const applyDiscount = (promo: any, price: number): number => {
    if (+promo.promo_fixed) return price - +promo.promo_fixed;
    if (+promo.promo_percent) return price * (100 - +promo.promo_percent) / 100;
    if (+promo.promo_pp) return +promo.promo_pp;

    return price;
};

export const format = (num: number, currency: string): string => {
    const symbol = getCurrencySymbol(currency, 'narrow');

    return formatCurrency(num, 'en-GB', symbol, currency);
};

export const filterOptions = (pack: Package, date?: Date, session?: Availability, message?: string): any[] => {
    const price = session && session.from_price ? +session.from_price : +pack.price;

    if (!pack || !pack.price_options || !pack.price_options.length) {
        return [{ name: 'Participants', qty_requested: 0, price, _price: price }];
    }

    let dayNum: number;

    if (date) dayNum = date.getDay() - 1;
    if (dayNum === -1) dayNum = 6;

    const options = pack.price_options.filter(item => {
        const avail = { day: true, session: true };

        if (item.dow && item.dow.length && date) {
            avail.day = item.dow.charAt(dayNum) !== '-';
        }

        if (item.sessions && item.sessions.length && session && session.session_id) {
            avail.session = !!item.sessions.find(val => (val.session_id || val.id) === session.session_id);
        }

        return avail.day && avail.session;
    });

    if (message) console.log(message, session.session_name, options, dayNum);

    if (options.length) {
        return options.map((item, i) => ({
            ...item,
            qty_requested: 0,
            price: item.price || price,
            _price: item.price || price
        }));
    }

    return [{ name: 'Participants', qty_requested: 0, price, _price: price }];
};

export const getOptionsMinMax = (currency: string, pack: Package, date?: Date, session?: Availability): {
    price: string, min: number, max: number
} => {
    const prices = filterOptions(pack, date, session).reduce((acc, opt) => {
        let alts = [];

        if (opt.alts) alts = opt.alts.map(alt => +(alt.price_calc || alt.price));

        acc = [...acc, ...alts, +(opt.price_calc || opt.price)];

        return acc;
    }, []);

    const min = Math.min(...prices);
    const max = Math.max(...prices);

    return {
        price: min === max ? format(min, currency) : format(min, currency) + ' - ' + format(max, currency),
        min,
        max
    };
};

export const getParameterByName = (name: string, url?: string) => {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');

    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);

    if (!results || !results[2]) return null;

    return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const applyPromo = (promo, date, venue, pack, session, priceOptions, qty, sessionPromo = false) => {
    if (!promo) {
        return priceOptions.map(item => {
            if (item.alts) item.alts = item.alts.map(val => ({ ...val, price_calc: null }));

            return { ...item, price_calc: null, _price_calc: null };
        });
    }

    if (!+promo.bookings || (+promo.admin && !sessionPromo)) return 'Promocode is invalid';

    const eventDate = moment(date);

    if (promo.lead && session.start_time) {
        const userTime = moment().tz(venue.timezone);
        const sessionTime = moment.tz(`${eventDate.format('YYYY-MM-DD')} ${session.start_time}`, 'YYYY-MM-DD HH:mm', venue.timezone).subtract(+promo.lead, 'minutes');

        if (sessionTime.isBefore(userTime)) return 'Promocode is invalid lead time';
    }

    if (
        (promo.venue_id && venue.venue_id !== promo.venue_id)
        || (promo.venue_ids && promo.venue_ids.length && !promo.venue_ids.includes(venue.venue_id))
    ) return 'Promocode is not valid for this venue';

    if (
        (promo.activity_id && pack.activity_id !== promo.activity_id)
        || (promo.activity_ids && promo.activity_ids.length && !promo.activity_ids.includes(pack.activity_id))
    ) return 'Promocode is not valid for this activity';

    if (
        (promo.package_id && pack.package_id !== promo.package_id)
        || (promo.package_ids && promo.package_ids.length && !promo.package_ids.includes(pack.package_id))
    ) return 'Promocode is not valid for this package';

    if (
        (promo.session_id && session.session_id !== promo.session_id)
        || (promo.session_ids && promo.session_ids.length && !promo.session_ids.includes(session.session_id))
    ) return 'Promocode is not valid for this session';

    const redemptionFrom = promo.start_date ? moment(promo.start_date) : null;
    const redemptionTo = promo.end_date ? moment(promo.end_date) : null;
    const eventFrom = promo.start_date_event ? moment(promo.start_date_event) : null;
    const eventTo = promo.end_date_event ? moment(promo.end_date_event) : null;
    const weekday = moment(date).isoWeekday() - 1;

    if (promo.dow && promo.dow.charAt(weekday) === '-') return 'This promocode is not valid on this day of the week';
    if (promo.min_pax && +promo.min_pax > qty) return 'This promocode is not valid on this group size';
    if (redemptionFrom && moment().isBefore(redemptionFrom)) return `This promocode is valid from ${redemptionFrom.format('DD MMM YYYY')}`;
    if (redemptionTo && moment().isAfter(redemptionTo)) return `This promocode is valid until ${redemptionTo.format('DD MMM YYYY')}`;
    if (eventFrom && eventDate.isBefore(eventFrom)) return `This promocode is valid for bookings after ${eventFrom.format('DD MMM YYYY')}`;
    if (eventTo && eventDate.isAfter(eventTo)) return `This promocode is valid for bookings before ${eventTo.format('DD MMM YYYY')}`;

    if (promo.exclusions && promo.exclusions.length) {
        const eventDateStr = eventDate.format('YYYY-MM-DD');
        const eventDateDay = eventDate.format('dddd');

        const excluded = promo.exclusions.reduce((acc, item) => {
            if (acc) return acc;

            return item.date === eventDateStr || item.days.includes(eventDateDay);
        }, false);

        if (excluded) return `This promo is not valid on ${eventDate.format('DD MMM YYYY')}`;
    }

    const maxDiscount = +promo.max_discount;
    const total = priceOptions.reduce((acc, item) => acc + item.price * item.qty_requested, 0);

    let priceOptionsCalc = priceOptions.map(item => {
        if (item.alts) item.alts = item.alts.map(val => ({ ...val, price_calc: applyDiscount(promo, +val.price) }));

        return {
            ...item,
            price_calc: applyDiscount(promo, +item.price),
            _price_calc: applyDiscount(promo, +item._price)
        };
    });

    const discount = total - priceOptionsCalc.reduce((acc, item) => acc + item.qty_requested * item.price_calc, 0);

    if (maxDiscount > 0 && discount > maxDiscount) {
        const maxPromo = { promo_percent: maxDiscount / total * 100 };

        priceOptionsCalc = priceOptions.map(item => {
            if (item.alts) item.alts = item.alts.map(val => ({ ...val, price_calc: applyDiscount(maxPromo, +val.price) }));

            return {
                ...item,
                price_calc: applyDiscount(maxPromo, +item.price),
                _price_calc: applyDiscount(maxPromo, +item._price)
            };
        });
    }

    return priceOptionsCalc;
}